import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import ApiEndpoints from './services/ApiEndpoints';
import './Admin.css';

function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [activeTab, setActiveTab] = useState('products');
  const [page, setPage] = useState(1);
  const [perPage] = useState(5);
  const [sortField, setSortField] = useState('id');
  const [sortDir, setSortDir] = useState('desc');
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    price: '',
    discountPercentage: '',
    discountedPrice: '',
    stock: '',
    inStock: true,
    published: true,
    description: '',
    tags: '',
    images: ['']
  });
  const [uploadingImages, setUploadingImages] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    color: '#ff1493',
    icon: '❤️'
  });


  const availableTags = ['Popular', 'Premium', 'Económica', 'Más vendida', 'Nuevo', 'Limitado'];
  const availableIcons = ['❤️', '👥', '🎉', '💼', '🎨', '🎁', '⭐', '🌟', '✨', '🎊', '🎈', '💝', '🎀', '🏆', '🌈', '🕯️', '💍', '👜', '🧴', '🌸', '🧦', '🍫', '🍪', '🍬'];

  // Backup function
  const createBackup = (data, filename) => {
    const dataStr = JSON.stringify(data, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', `backup_${filename}_${new Date().toISOString().split('T')[0]}.json`);
    linkElement.click();
    
    console.log(`Backup creado: backup_${filename}_${new Date().toISOString().split('T')[0]}.json`);
  };

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []);

  const loadProducts = async () => {
    console.log('🔥 Admin.loadProducts() - Calling ApiEndpoints');
    try {
      const data = await ApiEndpoints.getProducts();
      setProducts(data);
    } catch (error) {
      console.error('❌ Error loading products:', error);
    }
  };

  const loadCategories = async () => {
    console.log('🔥 Admin.loadCategories() - Calling ApiEndpoints');
    try {
      const data = await ApiEndpoints.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('❌ Error loading categories:', error);
    }
  };

  const saveProducts = async (updatedProducts, newProduct = null) => {
    try {
      console.log('🔥 Admin.saveProducts() - Saving to database');
      
      if (newProduct) {
        // Creating new product
        const result = await ApiEndpoints.createProduct(newProduct);
        if (result) {
          console.log('✅ Product created in DB:', result);
          setProducts([...products, result]);
          alert('✅ Producto creado exitosamente');
        } else {
          throw new Error('No se pudo crear el producto');
        }
      } else {
        // Updating existing products
        for (const product of updatedProducts) {
          if (products.find(p => p.id === product.id)) {
            // Product exists, update it
            const result = await ApiEndpoints.updateProduct(product.id, product);
            if (!result) {
              console.error('❌ Error updating product:', product.id);
            }
          }
        }
        setProducts(updatedProducts);
        alert('✅ Productos actualizados exitosamente');
      }
    } catch (error) {
      console.error('❌ Error saving products:', error);
      alert('❌ Error al guardar productos: ' + error.message);
    }
  };

  const saveCategories = async (updatedCategories, newCategory = null) => {
    try {
      console.log('🔥 Admin.saveCategories() - Saving to database');
      
      if (newCategory) {
        // Creating new category
        const result = await ApiEndpoints.createCategory(newCategory);
        if (result) {
          console.log('✅ Category created in DB:', result);
          setCategories([...categories, result]);
          alert('✅ Categoría creada exitosamente');
        } else {
          throw new Error('No se pudo crear la categoría');
        }
      } else {
        // Updating existing categories
        for (const category of updatedCategories) {
          if (categories.find(c => c.id === category.id)) {
            const result = await ApiEndpoints.updateCategory(category.id, category);
            if (!result) {
              console.error('❌ Error updating category:', category.id);
            }
          }
        }
        setCategories(updatedCategories);
        alert('✅ Categorías actualizadas exitosamente');
      }
    } catch (error) {
      console.error('❌ Error saving categories:', error);
      alert('❌ Error al guardar categorías: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      price: '',
      discountPercentage: '',
      discountedPrice: '',
      stock: '',
      inStock: true,
      published: true,
      description: '',
      details: '',
      tags: '',
      images: ['']
    });
    setEditingProduct(null);
    setShowForm(false);
  };

  const resetCategoryForm = () => {
    setCategoryFormData({
      name: '',
      description: '',
      color: '#ff1493',
      icon: '❤️'
    });
    setEditingCategory(null);
    setShowCategoryForm(false);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    
    const categoryData = {
      ...categoryFormData,
      active: true
    };

    if (editingCategory) {
      // Update existing category in database
      try {
        const result = await ApiEndpoints.updateCategory(editingCategory.id, categoryData);
        if (result) {
          const updatedCategories = categories.map(c => 
            c.id === editingCategory.id ? result : c
          );
          setCategories(updatedCategories);
          alert('✅ Categoría actualizada exitosamente');
        } else {
          throw new Error('Error al actualizar');
        }
      } catch (error) {
        console.error('❌ Error updating category:', error);
        alert('❌ Error al actualizar categoría');
      }
    } else {
      // Create new category in database
      saveCategories(categories, categoryData);
    }
    
    resetCategoryForm();
  };

  const handleEditCategory = (category) => {
    setCategoryFormData({
      name: category.name,
      description: category.description,
      color: category.color,
      icon: category.icon
    });
    setEditingCategory(category);
    setShowCategoryForm(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('¿Estás seguro de eliminar esta categoría? Los productos en esta categoría no serán eliminados.')) {
      try {
        const result = await ApiEndpoints.deleteCategory(categoryId);
        if (result) {
          const updatedCategories = categories.filter(c => c.id !== categoryId);
          setCategories(updatedCategories);
          alert('✅ Categoría eliminada exitosamente');
        } else {
          throw new Error('Error al eliminar');
        }
      } catch (error) {
        console.error('❌ Error deleting category:', error);
        alert('❌ Error al eliminar categoría');
      }
    }
  };

  const toggleCategoryActive = async (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (category) {
      try {
        const result = await ApiEndpoints.updateCategory(categoryId, { active: !category.active });
        if (result) {
          const updatedCategories = categories.map(c => 
            c.id === categoryId ? result : c
          );
          setCategories(updatedCategories);
        }
      } catch (error) {
        console.error('❌ Error toggling category:', error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Convertir nombres de propiedades al formato de la base de datos
    const categoryObj = categories.find(c => c.name === formData.category);
    const categoryId = categoryObj ? categoryObj.id : null;
    
    const productData = {
      name: formData.name,
      category_id: categoryId,
      price: parseInt(formData.price),
      tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
      images: formData.images.filter(img => img.trim()),
      description: formData.description,
      details: formData.details || '',
      stock: parseInt(formData.stock) || 0,
      published: formData.published
    };

    // Add discount fields only if they have values
    if (formData.discountPercentage && formData.discountedPrice) {
      productData.discount_percentage = parseInt(formData.discountPercentage);
      productData.discounted_price = parseInt(formData.discountedPrice);
    }

    if (editingProduct) {
      // Update existing product in database
      try {
        const result = await ApiEndpoints.updateProduct(editingProduct.id, productData);
        if (result) {
          const updatedProducts = products.map(p => 
            p.id === editingProduct.id ? result : p
          );
          setProducts(updatedProducts);
          alert('✅ Producto actualizado exitosamente');
        } else {
          throw new Error('Error al actualizar');
        }
      } catch (error) {
        console.error('❌ Error updating product:', error);
        alert('❌ Error al actualizar producto');
      }
    } else {
      // Create new product in database
      saveProducts(products, productData);
    }
    
    resetForm();
  };

  const handleEdit = (product) => {
    // Convertir nombres de columnas de la BD a nombres del formulario
    const categoryObj = categories.find(c => c.id === product.category_id);
    
    setFormData({
      name: product.name,
      category: categoryObj?.name || '',
      price: product.price?.toString() || '',
      discountPercentage: product.discount_percentage?.toString() || '',
      discountedPrice: product.discounted_price?.toString() || '',
      stock: product.stock?.toString() || '',
      inStock: product.in_stock ?? true,
      published: product.published ?? true,
      description: product.description || '',
      details: product.details || '',
      tags: Array.isArray(product.tags) ? product.tags.join(', ') : '',
      images: Array.isArray(product.images) ? product.images : ['']
    });
    setEditingProduct(product);
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (window.confirm('¿Estás seguro de eliminar este producto?')) {
      try {
        const result = await ApiEndpoints.deleteProduct(productId);
        if (result) {
          const updatedProducts = products.filter(p => p.id !== productId);
          setProducts(updatedProducts);
          alert('✅ Producto eliminado exitosamente');
        } else {
          throw new Error('Error al eliminar');
        }
      } catch (error) {
        console.error('❌ Error deleting product:', error);
        alert('❌ Error al eliminar producto');
      }
    }
  };

  const addImageField = () => {
    setFormData({
      ...formData,
      images: [...formData.images, '']
    });
  };

  const insertHtmlTag = (tag) => {
    const textarea = document.getElementById('details-editor');
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.details || '';
    const selectedText = text.substring(start, end);
    
    let newText;
    let cursorOffset;
    
    switch (tag) {
      case 'br':
        newText = text.substring(0, start) + '<br>' + text.substring(end);
        cursorOffset = 4;
        break;
      case 'hr':
        newText = text.substring(0, start) + '<hr>' + text.substring(end);
        cursorOffset = 4;
        break;
      case 'ul':
        newText = text.substring(0, start) + '<ul>\n  <li>' + (selectedText || 'Elemento') + '</li>\n</ul>' + text.substring(end);
        cursorOffset = 10;
        break;
      case 'ol':
        newText = text.substring(0, start) + '<ol>\n  <li>' + (selectedText || 'Elemento') + '</li>\n</ol>' + text.substring(end);
        cursorOffset = 10;
        break;
      case 'li':
        newText = text.substring(0, start) + '<li>' + selectedText + '</li>' + text.substring(end);
        cursorOffset = 4;
        break;
      case 'h3':
        newText = text.substring(0, start) + '<h3>' + (selectedText || 'Título') + '</h3>' + text.substring(end);
        cursorOffset = 5;
        break;
      case 'p':
        newText = text.substring(0, start) + '<p>' + (selectedText || 'Párrafo') + '</p>' + text.substring(end);
        cursorOffset = 3;
        break;
      case 'div':
        newText = text.substring(0, start) + '<div>\n  ' + (selectedText || 'Contenido') + '\n</div>' + text.substring(end);
        cursorOffset = 12;
        break;
      case 'span':
        newText = text.substring(0, start) + '<span style="color: #ff1493;">' + (selectedText || 'Texto') + '</span>' + text.substring(end);
        cursorOffset = 30;
        break;
      default:
        newText = text.substring(0, start) + '<' + tag + '>' + selectedText + '</' + tag + '>' + text.substring(end);
        cursorOffset = tag.length + 2;
    }
    
    setFormData({ ...formData, details: newText });
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + cursorOffset, start + cursorOffset + selectedText.length);
    }, 0);
  };

  const insertTemplate = (templateType) => {
    const textarea = document.getElementById('details-editor');
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const text = formData.details || '';
    
    let newText;
    
    switch (templateType) {
      case 'check':
        newText = text.substring(0, start) + 
`<ul>
  <li>✓ Opción 1</li>
  <li>✓ Opción 2</li>
  <li>✓ Opción 3</li>
</ul>` + text.substring(start);
        break;
      case 'includes':
        newText = text.substring(0, start) + 
`<div style="background: #f0fdf4; padding: 15px; border-radius: 8px; border-left: 4px solid #22c55e;">
  <h3 style="margin: 0 0 10px 0; color: #166534;">🎁 Incluye:</h3>
  <ul style="margin: 0; padding-left: 20px; color: #166534;">
    <li>Producto 1</li>
    <li>Producto 2</li>
    <li>Producto 3</li>
  </ul>
</div>` + text.substring(start);
        break;
      case 'features':
        newText = text.substring(0, start) + 
`<div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin: 10px 0;">
  <div style="background: #f8fafc; padding: 10px; border-radius: 6px; text-align: center;">
    <div style="font-size: 24px;">⭐</div>
    <div style="font-weight: bold;">Característica 1</div>
  </div>
  <div style="background: #f8fafc; padding: 10px; border-radius: 6px; text-align: center;">
    <div style="font-size: 24px;">🎁</div>
    <div style="font-weight: bold;">Característica 2</div>
  </div>
  <div style="background: #f8fafc; padding: 10px; border-radius: 6px; text-align: center;">
    <div style="font-size: 24px;">💝</div>
    <div style="font-weight: bold;">Característica 3</div>
  </div>
  <div style="background: #f8fafc; padding: 10px; border-radius: 6px; text-align: center;">
    <div style="font-size: 24px;">🚚</div>
    <div style="font-weight: bold;">Característica 4</div>
  </div>
</div>` + text.substring(start);
        break;
      default:
        return;
    }
    
    setFormData({ ...formData, details: newText });
    
    setTimeout(() => {
      textarea.focus();
    }, 0);
  };

  const updateImageField = (index, value) => {
    const updatedImages = [...formData.images];
    updatedImages[index] = value;
    setFormData({
      ...formData,
      images: updatedImages
    });
  };

  const removeImageField = (index) => {
    if (formData.images.length > 1) {
      const updatedImages = formData.images.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        images: updatedImages
      });
    }
  };

  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploadingImages(true);
    try {
      const uploadedUrls = [];
      
      for (let file of files) {
        const result = await ApiEndpoints.uploadImage(file);
        // uploadImage retorna la URL directamente o null
        if (result) {
          uploadedUrls.push(result);
          console.log('✅ Imagen subida:', result);
        } else {
          console.error('❌ Error al subir imagen:', file.name);
        }
      }
      
      if (uploadedUrls.length === 0) {
        alert('No se pudieron subir las imágenes. Verifica la conexión.');
        return;
      }
      
      // Add uploaded URLs to existing images
      const existingImages = formData.images.filter(img => img.trim());
      const updatedImages = [...existingImages, ...uploadedUrls];
      
      console.log('📸 Imágenes actualizadas:', updatedImages);
      setFormData({
        ...formData,
        images: updatedImages
      });
      
      alert(`✅ ${uploadedUrls.length} imagen(es) subida(s) exitosamente`);
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Error al subir imágenes: ' + error.message);
    } finally {
      setUploadingImages(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleImageUpload(files);
    e.target.value = ''; // Clear input for repeated uploads
  };

  const formatPrice = (price) => {
    return "$" + price.toLocaleString('es-CO');
  };

  const sortedProducts = [...products].sort((a, b) => {
    const aVal = sortField === 'name' ? (a.name || '').toLowerCase() : sortField === 'price' ? a.price || 0 : sortField === 'stock' ? a.stock || 0 : sortField === 'created_at' ? new Date(a.created_at || 0).getTime() : a.id;
    const bVal = sortField === 'name' ? (b.name || '').toLowerCase() : sortField === 'price' ? b.price || 0 : sortField === 'stock' ? b.stock || 0 : sortField === 'created_at' ? new Date(b.created_at || 0).getTime() : b.id;
    if (sortDir === 'asc') return aVal > bVal ? 1 : -1;
    return aVal < bVal ? 1 : -1;
  });
  const totalPages = Math.ceil(sortedProducts.length / perPage);
  const currentProducts = sortedProducts.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="https://cdn.abacus.ai/images/ce62aaea-7192-4eab-8134-3539efec6757.png" alt="GiftBox Logo" className="w-10 h-10 rounded-lg object-cover" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Panel de Administración</h1>
              <p className="text-sm text-gray-500">GiftBox Management</p>
            </div>
          </div>
          <div className="flex gap-3">
            <a href="/" className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition">
              🏪 Ver Tienda
            </a>
            <Link to="/orders" className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition">
              📋 Pedidos
            </Link>
            <button 
              onClick={() => createBackup(products, 'products')}
              className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition"
            >
              📄 Backup Productos
            </button>
            <button 
              onClick={() => setShowForm(true)}
              className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition"
            >
              ➕ Nuevo Producto
            </button>
            <button 
              onClick={() => createBackup(categories, 'categories')}
              className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition"
            >
              📄 Backup Categorías
            </button>
            <button 
              onClick={() => {
                logout();
                navigate('/login');
              }}
              className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition"
            >
              🚪 Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b">
          <button
            onClick={() => setActiveTab('products')}
            className={`pb-4 px-2 font-medium transition ${
              activeTab === 'products'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            📦 Productos
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`pb-4 px-2 font-medium transition ${
              activeTab === 'categories'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            🏷️ Categorías
          </button>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 rounded-xl shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Productos</h3>
            <p className="text-3xl font-bold text-blue-600">{products.length}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow">
            <h3 className="text-sm font-medium text-gray-500">Categorías</h3>
            <p className="text-3xl font-bold text-green-600">{categories.length}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow">
            <h3 className="text-sm font-medium text-gray-500">Precio Promedio</h3>
            <p className="text-3xl font-bold text-purple-600">
              {products.length > 0 ? formatPrice(Math.round(products.reduce((sum, p) => sum + p.price, 0) / products.length)) : '$0'}
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow">
            <h3 className="text-sm font-medium text-gray-500">Producto más caro</h3>
            <p className="text-xl font-bold text-red-600">
              {products.length > 0 ? formatPrice(Math.max(...products.map(p => p.price))) : '$0'}
            </p>
          </div>
        </div>

        {/* Contenido dinámico según tab */}
        {activeTab === 'products' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800">Productos</h2>
              <button 
                onClick={() => setShowForm(true)}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition"
              >
                ➕ Nuevo Producto
              </button>
            </div>
            <div className="bg-white rounded-xl shadow">
              {products.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="text-6xl mb-4">📦</div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No hay productos</h3>
                  <p className="text-gray-500 mb-6">Comienza agregando tu primer producto</p>
                  <button 
                    onClick={() => setShowForm(true)}
                    className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition"
                  >
                    ➕ Agregar Producto
                  </button>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-pink-500" onClick={() => { setSortField('id'); setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }}>ID {sortField === 'id' ? (sortDir === 'asc' ? '↑' : '↓') : ''}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-pink-500" onClick={() => { setSortField('name'); setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }}>Producto {sortField === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : ''}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categoría</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-pink-500" onClick={() => { setSortField('price'); setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }}>Precio {sortField === 'price' ? (sortDir === 'asc' ? '↑' : '↓') : ''}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descuento</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-pink-500" onClick={() => { setSortField('stock'); setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }}>Stock {sortField === 'stock' ? (sortDir === 'asc' ? '↑' : '↓') : ''}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Etiquetas</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Imágenes</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentProducts.map((product) => (
                        <tr key={product.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm text-gray-500">#{product.id}</td>
                          <td className="px-6 py-4">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{product.name}</div>
                              <div className="text-sm text-gray-500">{product.description}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {(() => {
                              const category = categories.find(c => c.id === product.category_id);
                              return (
                                <span 
                                  className="px-2 py-1 text-xs rounded-full text-white"
                                  style={{ backgroundColor: category?.color || '#6b7280' }}
                                >
                                  {category?.name || 'Sin categoría'}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {formatPrice(product.price)}
                            {product.discounted_price && (
                              <div className="text-green-600 font-bold">
                                {formatPrice(product.discounted_price)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {product.discount_percentage ? (
                              <div>
                                <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                                  -{product.discount_percentage}%
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <div>
                              <span className={`font-medium ${product.in_stock ? 'text-green-600' : 'text-red-600'}`}>
                                {product.stock} unidades
                              </span>
                              {!product.in_stock && (
                                <div className="text-xs text-red-500">Sin stock</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <div className="space-y-1">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                product.in_stock ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {product.in_stock ? '✅ En stock' : '❌ Sin stock'}
                              </span>
                              <span className={`px-2 py-1 text-xs rounded-full block ${
                                product.published ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {product.published ? '👁️ Publicado' : '🚫 Oculto'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1">
                              {product.tags.map((tag, index) => (
                                <span key={index} className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {product.images.length}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium">
                            <button 
                              onClick={() => handleEdit(product)}
                              className="text-blue-600 hover:text-blue-900 mr-3"
                            >
                              ✏️ Editar
                            </button>
                            <button 
                              onClick={() => handleDelete(product.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              🗑️ Eliminar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 flex flex-col md:flex-row justify-between items-center gap-4 px-4 pb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Ordenar:</span>
                    <select value={sortField} onChange={(e) => setSortField(e.target.value)} className="p-2 border rounded text-sm">
                      <option value="id">ID</option>
                      <option value="name">Nombre</option>
                      <option value="price">Precio</option>
                      <option value="stock">Stock</option>
                      <option value="created_at">Fecha</option>
                    </select>
                    <button onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')} className="p-2 border rounded hover:bg-gray-50">
                      {sortDir === 'asc' ? '↑' : '↓'}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">{Math.min((page - 1) * perPage + 1, sortedProducts.length)}-{Math.min(page * perPage, sortedProducts.length)} de {sortedProducts.length}</span>
                    <div className="flex gap-1">
                      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50">‹</button>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let num = totalPages <= 5 ? i + 1 : page <= 3 ? i + 1 : page >= totalPages - 2 ? totalPages - 4 + i : page - 2 + i;
                        return <button key={num} onClick={() => setPage(num)} className={`px-3 py-1 border rounded ${page === num ? 'bg-pink-500 text-white' : 'hover:bg-gray-50'}`}>{num}</button>;
                      })}
                      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50">›</button>
                    </div>
                  </div>
                </div>
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'categories' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800">Categorías</h2>
              <button 
                onClick={() => setShowCategoryForm(true)}
                className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition"
              >
                ➕ Nueva Categoría
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((category) => (
                <div key={category.id} className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
                        style={{ backgroundColor: category.color + '20', color: category.color }}
                      >
                        {category.icon}
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-800">{category.name}</h3>
                        <p className="text-sm text-gray-500">{category.description}</p>
                      </div>
                    </div>
                    <div className={`w-3 h-3 rounded-full ${category.active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleEditCategory(category)}
                      className="flex-1 bg-blue-100 text-blue-700 py-2 rounded-lg hover:bg-blue-200 transition text-sm"
                    >
                      ✏️ Editar
                    </button>
                    <button 
                      onClick={() => toggleCategoryActive(category.id)}
                      className={`flex-1 py-2 rounded-lg transition text-sm ${
                        category.active 
                          ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' 
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {category.active ? '🚫 Desactivar' : '✅ Activar'}
                    </button>
                    <button 
                      onClick={() => handleDeleteCategory(category.id)}
                      className="bg-red-100 text-red-700 px-3 py-2 rounded-lg hover:bg-red-200 transition text-sm"
                    >
                      🗑️
                    </button>
                  </div>
                  <div className="mt-3 text-sm text-gray-500">
                    Productos: {products.filter(p => p.category_id === category.id).length}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* Modal Formulario Producto */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-800">
                {editingProduct ? '✏️ Editar Producto' : '➕ Nuevo Producto'}
              </h3>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre del Producto</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ej: GiftBox Amor Infinito"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Categoría</label>
                  <select
                    required
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Seleccionar categoría</option>
                    {categories.filter(c => c.active).map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.icon} {cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Precio (COP)</label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="189900"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">% Descuento (opcional)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.discountPercentage}
                      onChange={(e) => {
                        const discount = e.target.value;
                        const price = parseInt(formData.price) || 0;
                        const discountAmount = (price * discount) / 100;
                        const finalPrice = price - discountAmount;
                        setFormData({
                          ...formData,
                          discountPercentage: discount,
                          discountedPrice: discount ? Math.round(finalPrice).toString() : ''
                        });
                      }}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="20"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Precio con Descuento (opcional)</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.discountedPrice}
                      onChange={(e) => setFormData({...formData, discountedPrice: e.target.value})}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="151920"
                      readOnly={formData.discountPercentage}
                    />
                    {formData.discountPercentage && (
                      <p className="text-xs text-green-600 mt-1">Calculado automáticamente</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Stock</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.stock}
                      onChange={(e) => {
                        const stock = e.target.value;
                        setFormData({
                          ...formData,
                          stock: stock,
                          inStock: parseInt(stock) > 0
                        });
                      }}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="15"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.inStock}
                          onChange={(e) => setFormData({...formData, inStock: e.target.checked})}
                          className="mr-2"
                        />
                        <span className="text-sm">En stock</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.published}
                          onChange={(e) => setFormData({...formData, published: e.target.checked})}
                          className="mr-2"
                        />
                        <span className="text-sm">Publicado</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
                  <textarea
                    required
                    rows="3"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Describe el producto..."
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Detalles del Producto (HTML)
                    <span className="text-xs text-gray-500 ml-2">(Formato enriquecido)</span>
                  </label>
                  <div className="border rounded-lg overflow-hidden">
                    {/* Toolbar */}
                    <div className="bg-gray-100 p-2 flex flex-wrap gap-1 border-b">
                      <button
                        type="button"
                        onClick={() => insertHtmlTag('b')}
                        className="px-3 py-1.5 bg-white border rounded hover:bg-gray-50 text-sm font-bold min-w-[32px]"
                        title="Negrita"
                      >
                        B
                      </button>
                      <button
                        type="button"
                        onClick={() => insertHtmlTag('i')}
                        className="px-3 py-1.5 bg-white border rounded hover:bg-gray-50 text-sm italic min-w-[32px]"
                        title="Cursiva"
                      >
                        I
                      </button>
                      <button
                        type="button"
                        onClick={() => insertHtmlTag('u')}
                        className="px-3 py-1.5 bg-white border rounded hover:bg-gray-50 text-sm underline min-w-[32px]"
                        title="Subrayado"
                      >
                        U
                      </button>
                      <button
                        type="button"
                        onClick={() => insertHtmlTag('s')}
                        className="px-3 py-1.5 bg-white border rounded hover:bg-gray-50 text-sm line-through min-w-[32px]"
                        title="Tachado"
                      >
                        S
                      </button>
                      <div className="w-px h-6 bg-gray-300 mx-1"></div>
                      <button
                        type="button"
                        onClick={() => insertHtmlTag('h3')}
                        className="px-3 py-1.5 bg-white border rounded hover:bg-gray-50 text-sm font-bold min-w-[32px]"
                        title="Título"
                      >
                        H
                      </button>
                      <button
                        type="button"
                        onClick={() => insertHtmlTag('p')}
                        className="px-3 py-1.5 bg-white border rounded hover:bg-gray-50 text-sm min-w-[32px]"
                        title="Párrafo"
                      >
                        P
                      </button>
                      <div className="w-px h-6 bg-gray-300 mx-1"></div>
                      <button
                        type="button"
                        onClick={() => insertHtmlTag('ul')}
                        className="px-3 py-1.5 bg-white border rounded hover:bg-gray-50 text-sm min-w-[32px]"
                        title="Lista con viñetas"
                      >
                        •
                      </button>
                      <button
                        type="button"
                        onClick={() => insertHtmlTag('ol')}
                        className="px-3 py-1.5 bg-white border rounded hover:bg-gray-50 text-sm min-w-[32px]"
                        title="Lista numerada"
                      >
                        #
                      </button>
                      <button
                        type="button"
                        onClick={() => insertHtmlTag('li')}
                        className="px-3 py-1.5 bg-white border rounded hover:bg-gray-50 text-sm min-w-[32px]"
                        title="Elemento de lista"
                      >
                        -
                      </button>
                      <div className="w-px h-6 bg-gray-300 mx-1"></div>
                      <button
                        type="button"
                        onClick={() => insertHtmlTag('br')}
                        className="px-3 py-1.5 bg-white border rounded hover:bg-gray-50 text-sm min-w-[32px]"
                        title="Salto de línea"
                      >
                        ↵
                      </button>
                      <button
                        type="button"
                        onClick={() => insertHtmlTag('hr')}
                        className="px-3 py-1.5 bg-white border rounded hover:bg-gray-50 text-sm min-w-[32px]"
                        title="Línea horizontal"
                      >
                        —
                      </button>
                      <button
                        type="button"
                        onClick={() => insertHtmlTag('span')}
                        className="px-3 py-1.5 bg-white border rounded hover:bg-gray-50 text-sm min-w-[32px]"
                        title="Texto colored"
                      >
                        A
                      </button>
                      <div className="w-px h-6 bg-gray-300 mx-1"></div>
                      <button
                        type="button"
                        onClick={() => insertHtmlTag('div')}
                        className="px-3 py-1.5 bg-white border rounded hover:bg-gray-50 text-sm min-w-[32px]"
                        title="Bloque div"
                      >
                        []
                      </button>
                      <button
                        type="button"
                        onClick={() => insertHtmlTag('strong')}
                        className="px-3 py-1.5 bg-white border rounded hover:bg-gray-50 text-sm font-bold min-w-[32px]"
                        title="Texto importante"
                      >
                        !
                      </button>
                      <button
                        type="button"
                        onClick={() => insertHtmlTag('em')}
                        className="px-3 py-1.5 bg-white border rounded hover:bg-gray-50 text-sm italic min-w-[32px]"
                        title="Énfasis"
                      >
                        ~
                      </button>
                      <div className="w-px h-6 bg-gray-300 mx-1"></div>
                      <button
                        type="button"
                        onClick={() => insertTemplate('check')}
                        className="px-3 py-1.5 bg-green-50 border border-green-200 rounded hover:bg-green-100 text-sm min-w-[32px]"
                        title="Lista con checks"
                      >
                        ☑
                      </button>
                      <button
                        type="button"
                        onClick={() => insertTemplate('includes')}
                        className="px-3 py-1.5 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 text-sm min-w-[32px]"
                        title="Plantilla incluye"
                      >
                        ✓
                      </button>
                      <button
                        type="button"
                        onClick={() => insertTemplate('features')}
                        className="px-3 py-1.5 bg-purple-50 border border-purple-200 rounded hover:bg-purple-100 text-sm min-w-[32px]"
                        title="Características"
                      >
                        ★
                      </button>
                    </div>
                    <textarea
                      id="details-editor"
                      rows="8"
                      value={formData.details || ''}
                      onChange={(e) => setFormData({...formData, details: e.target.value})}
                      className="w-full p-3 border-0 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                      placeholder="<b>Incluye:</b>&#10;<ul>&#10;  <li>Producto 1</li>&#10;  <li>Producto 2</li>&#10;</ul>&#10;<br>&#10;Más detalles..."
                    />
                  </div>
                  
                  {/* Vista previa */}
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-1">Vista previa:</p>
                    <div 
                      className="mt-1 p-4 border rounded bg-white text-sm min-h-[100px]"
                      dangerouslySetInnerHTML={{ __html: formData.details || '<span class="text-gray-400">La vista previa se mostrará aquí...</span>' }}
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Etiquetas (separadas por comas)</label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData({...formData, tags: e.target.value})}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Popular, Premium, Nuevo"
                  />
                  <div className="mt-2 flex flex-wrap gap-2">
                    {availableTags.map(tag => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => {
                          const currentTags = formData.tags.split(',').map(t => t.trim()).filter(t => t);
                          if (!currentTags.includes(tag)) {
                            setFormData({
                              ...formData,
                              tags: [...currentTags, tag].join(', ')
                            });
                          }
                        }}
                        className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200"
                      >
                        + {tag}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Imágenes del Producto</label>
                  
                  {/* File Upload Section */}
                  <div className="mb-4 p-4 border-2 border-dashed border-gray-300 rounded-lg">
                    <div className="text-center">
                      <div className="text-3xl mb-2">📷</div>
                      <p className="text-sm text-gray-600 mb-2">Sube imágenes desde tu computadora</p>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="file-upload"
                      />
                      <label
                        htmlFor="file-upload"
                        className={`inline-block px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 cursor-pointer transition ${
                          uploadingImages ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {uploadingImages ? '⏳ Subiendo...' : '📁 Seleccionar Archivos'}
                      </label>
                    </div>
                  </div>

                  {/* URL Input Section */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">O ingresa URLs de imágenes directamente:</p>
                    {formData.images.map((image, index) => (
                      <div key={index} className="flex gap-2 mb-2">
                        <input
                          type="url"
                          value={image}
                          onChange={(e) => updateImageField(index, e.target.value)}
                          className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="https://ejemplo.com/imagen.jpg"
                        />
                        {formData.images.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeImageField(index)}
                            className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addImageField}
                      className="mt-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                    >
                      ➕ Agregar URL de Imagen
                    </button>
                  </div>

                  {/* Image Preview Section */}
                  {formData.images.some(img => img.trim()) && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-2">Vista previa de imágenes:</p>
                      <div className="grid grid-cols-3 gap-2">
                        {formData.images.filter(img => img.trim()).map((image, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={image}
                              alt={`Preview ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg border"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/150x150?text=Error';
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => removeImageField(formData.images.indexOf(image))}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  className="flex-1 bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition"
                >
                  {editingProduct ? '💾 Actualizar Producto' : '➕ Crear Producto'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Formulario Categoría */}
      {showCategoryForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-800">
                {editingCategory ? '✏️ Editar Categoría' : '➕ Nueva Categoría'}
              </h3>
              <button onClick={resetCategoryForm} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>

            <form onSubmit={handleCategorySubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de la Categoría</label>
                  <input
                    type="text"
                    required
                    value={categoryFormData.name}
                    onChange={(e) => setCategoryFormData({...categoryFormData, name: e.target.value})}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Ej: Amor"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
                  <textarea
                    required
                    rows="2"
                    value={categoryFormData.description}
                    onChange={(e) => setCategoryFormData({...categoryFormData, description: e.target.value})}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Describe esta categoría..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={categoryFormData.color}
                      onChange={(e) => setCategoryFormData({...categoryFormData, color: e.target.value})}
                      className="w-16 h-10 border rounded"
                    />
                    <input
                      type="text"
                      value={categoryFormData.color}
                      onChange={(e) => setCategoryFormData({...categoryFormData, color: e.target.value})}
                      className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="#ff1493"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ícono</label>
                  <div className="grid grid-cols-8 gap-2">
                    {availableIcons.map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => setCategoryFormData({...categoryFormData, icon})}
                        className={`p-2 text-lg rounded border-2 transition ${
                          categoryFormData.icon === icon 
                            ? 'border-green-500 bg-green-50' 
                            : 'border-gray-200 hover:border-green-300'
                        }`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Vista Previa</label>
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
                      style={{ backgroundColor: categoryFormData.color + '20', color: categoryFormData.color }}
                    >
                      {categoryFormData.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800">{categoryFormData.name || 'Nombre'}</h4>
                      <p className="text-sm text-gray-500">{categoryFormData.description || 'Descripción'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  className="flex-1 bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 transition"
                >
                  {editingCategory ? '💾 Actualizar Categoría' : '➕ Crear Categoría'}
                </button>
                <button
                  type="button"
                  onClick={resetCategoryForm}
                  className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default Admin;